import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { NavItem, NavChild, NavGroup } from '../../config/navigation'

interface FlyoutMenuProps {
  item: NavItem
  anchorRect: DOMRect | null
  onClose: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  projectId: string
}

export default function FlyoutMenu({ item, anchorRect, onClose, onMouseEnter, onMouseLeave, projectId }: FlyoutMenuProps) {
  const navigate = useNavigate()
  const flyoutRef = useRef<HTMLDivElement>(null)
  const [topOffset, setTopOffset] = useState<number>(0)

  useEffect(() => {
    if (!anchorRect || !flyoutRef.current) return

    const flyoutHeight = flyoutRef.current.offsetHeight
    const viewportHeight = window.innerHeight
    const desiredTop = anchorRect.top

    if (desiredTop + flyoutHeight > viewportHeight - 8) {
      setTopOffset(viewportHeight - 8 - flyoutHeight)
    } else {
      setTopOffset(desiredTop)
    }
  }, [anchorRect])

  if (!anchorRect) return null

  const handleItemClick = (child: NavChild) => {
    if (child.disabled) return
    // Hors contexte projet (pages globales), projectId est absent : on renvoie vers
    // le sélecteur de projet au lieu d'un lien cassé `/p//...` (projectId vide → 500 API).
    navigate(projectId ? `/p/${projectId}/${child.path}` : '/projects')
    onClose()
  }

  // Resolve children source: prefer groups, fallback to children
  const groups = item.groups && item.groups.length > 0
    ? item.groups
    : item.children
      ? [{ name: undefined, items: item.children }]
      : []

  if (groups.length === 0) return null

  return (
    <div
      ref={flyoutRef}
      className="fixed z-40 bg-white border border-light-border rounded-lg shadow-lg py-2"
      style={{
        left: 73,
        top: topOffset || anchorRect.top,
        minWidth: 200,
        maxWidth: 280,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave ?? onClose}
    >
      {/* Header */}
      <div className="px-3 py-1.5 font-semibold text-[13px] text-[#1B2559]">
        {item.label}
      </div>

      {/* Groups */}
      {groups.map((group: NavGroup, groupIndex: number) => (
        <div key={groupIndex}>
          {group.name && (
            <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-[#8B8FA7]">
              {group.name}
            </div>
          )}
          {group.items.map((child: NavChild) => (
            <button
              key={child.id}
              onClick={() => handleItemClick(child)}
              disabled={child.disabled}
              className={[
                'w-full text-left block text-[13px] px-3 py-1.5 transition-colors',
                child.disabled
                  ? 'opacity-40 pointer-events-none text-[#555A73]'
                  : 'text-[#555A73] hover:bg-light-surface-hover cursor-pointer',
              ].join(' ')}
            >
              <span className="flex items-center justify-between gap-2">
                <span>{child.label}</span>
                {child.badge && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-500 shrink-0">
                    {child.badge}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}
